// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for recording one-off actions on an asset.
 * This module provides a simple interface for users to log individual
 * events or actions taken on an asset, such as maintenance checks,
 * inspections, or status updates. It handles form validation and
 * submission through a clean, focused UI.
 *
 * Key features:
 * - Event type selection
 * - Date picking
 * - Form validation
 * - Error handling
 * - Real-time updates
 */
import * as z from 'zod';
import { CreateEventParameters, createEvent } from '@/asset/api/create-event';
import { FormEventSearch } from '@/asset/components/form-event-search';
import { FormDatePicker } from '@/components/form-date-picker';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm } from 'react-hook-form';

/**
 * Interface for the raw form input data.
 * Represents the unvalidated state of the action form.
 *
 * @interface InputFormData
 */
interface InputFormData {
  /**
   * The selected event configuration.
   * Can be null when no event type is selected.
   */
  configuration: { type: { id: string } } | null;

  /**
   * The date when the action occurred.
   * Can be null when no date is selected.
   */
  date: DateTime | null;
}

/**
 * Schema for validating form output data.
 * Enforces required fields and data format.
 */
const outputSchema = z.object({
  configuration: z.object(
    { type: z.object({ id: z.string() }) },
    { message: 'Event is required' }
  ),
  date: dateSchema
    .nullable()
    .refine((date): date is DateTime => date !== null && date.isValid, {
      message: 'Event date is required',
    })
    .refine((date) => date >= MINIMUM_DATE, {
      message: 'Event date cannot be before January 1, 1970',
    })
    .refine((date) => date <= DateTime.now().plus({ days: 1 }).endOf('day'), {
      message: 'Event date cannot be in the future',
    }),
});

/**
 * Type definition for validated form output.
 * Used when submitting the form data.
 *
 * @typedef {Object} OutputFormData
 */
type OutputFormData = z.infer<typeof outputSchema>;

/**
 * Props for configuring the AssetActionDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * The ID of the asset to create an action for.
   */
  assetId: string;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for recording actions on an asset.
 * Provides a form interface for selecting an event type
 * and date, with validation and error handling.
 *
 * Features:
 * - Event type selection with autocomplete
 * - Date picking with validation
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView() {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <AssetActionDialog
 *       isOpen={isOpen}
 *       assetId="asset-123"
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function AssetActionDialog(props: Props) {
  const queryClient = useQueryClient();

  const createEventMutation = useMutation({
    mutationFn: (parameters: CreateEventParameters) =>
      createEvent(props.assetId, parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['asset', props.assetId, 'event'],
      });

      close();
    },
  });

  const { control, reset, handleSubmit } = useForm<
    InputFormData,
    unknown,
    OutputFormData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: { configuration: null, date: null },
  });

  /**
   * Handles form submission when validation passes.
   *
   * @param data - The validated form data
   */
  const handleValidSubmission: SubmitHandler<OutputFormData> = (data) => {
    createEventMutation.mutate({
      typeId: data.configuration.type.id,
      occurredAt: data.date,
    });
  };

  /**
   * Closes the dialog and resets the form state.
   */
  const close = () => {
    props.onClose();
    reset();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <DialogTitle>Action asset</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Typography variant="subtitle1">What</Typography>
            <FormEventSearch
              name="configuration"
              label="Event"
              control={control}
            />
          </Stack>
          <Stack spacing={2}>
            <Typography variant="subtitle1">When</Typography>
            <FormDatePicker
              disableFuture
              name="date"
              label="Event date"
              control={control}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          Action
        </Button>
      </DialogActions>
    </Dialog>
  );
}
